import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getList, updateList } from '../firebase/database';
import { ArrowLeft, Save, Users } from 'lucide-react';
import { SUPPORTED_CURRENCIES, getCurrencySymbol } from '../services/currencyService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

const EditList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isGroup: false,
    eventDate: '',
    defaultCurrency: 'NOK'
  });

  useEffect(() => {
    const loadList = async () => {
      const result = await getList(id);
      if (result.success) {
        setList(result.data);
        const initialFormData = {
          name: result.data.name || '',
          description: result.data.description || '',
          isGroup: result.data.isGroup || false,
          eventDate: result.data.eventDate ? 
            (result.data.eventDate.toDate ? 
              result.data.eventDate.toDate().toISOString().split('T')[0] : 
              new Date(result.data.eventDate).toISOString().split('T')[0]
            ) : '',
          defaultCurrency: result.data.defaultCurrency || 'NOK'
        };
        setFormData(initialFormData);
      } else {
        toast.error(t('editList.failedToLoadList'));
        navigate('/dashboard');
      }
      setLoading(false);
    };

    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t('editList.listNameRequired'));
      return;
    }

    setSaving(true);
    
    try {
      const result = await updateList(id, {
        name: formData.name.trim(),
        description: formData.description.trim(),
        isGroup: formData.isGroup,
        eventDate: formData.eventDate || null,
        defaultCurrency: formData.defaultCurrency,
        updatedAt: new Date()
      });

      if (result.success) {
        toast.success(t('editList.listUpdatedSuccess'));
        navigate(`/list/${id}/view`);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(t('editList.failedToUpdateList'));
    }
    
    setSaving(false);
  };

  if (loading) {
    return <LoadingSpinner text={t('editList.loadingList')} />;
  }

  if (!list || list.createdBy !== user?.uid) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('editList.accessDenied')}</h2>
          <p className="text-gray-600 mb-6">{t('editList.noPermission')}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary"
          >
            {t('editList.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/list/${id}/view`)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>{t('editList.backToList')}</span>
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('editList.title')}</h1>
        <p className="text-gray-600">{t('editList.subtitle')}</p>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('editList.listName')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              className="input-field"
              placeholder={t('editList.listNamePlaceholder')}
              value={formData.name || ''}
              onChange={handleChange}
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              {t('editList.description')}
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              className="input-field"
              placeholder={t('editList.descriptionPlaceholder')}
              value={formData.description || ''}
              onChange={handleChange}
            />
          </div>

          <div>
            <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-1">
              {t('editList.eventDate')}
            </label>
            <input
              type="date"
              id="eventDate"
              name="eventDate"
              className="input-field"
              value={formData.eventDate || ''}
              onChange={handleChange}
            />
          </div>

          <div>
            <label htmlFor="defaultCurrency" className="block text-sm font-medium text-gray-700 mb-1">
              {t('editList.defaultCurrency')}
            </label>
            <select
              id="defaultCurrency"
              name="defaultCurrency"
              className="input-field"
              value={formData.defaultCurrency}
              onChange={handleChange}
            >
              {SUPPORTED_CURRENCIES.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {getCurrencySymbol(currency.code)} {currency.name} ({currency.code})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {t('editList.currencyHelp')}
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isGroup"
              name="isGroup"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={formData.isGroup}
              onChange={handleChange}
            />
            <label htmlFor="isGroup" className="ml-2 block text-sm text-gray-700">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>{t('editList.groupList')}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('editList.groupListDesc')}
              </p>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(`/list/${id}/view`)}
              className="btn-secondary"
              disabled={saving}
            >
              {t('editList.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center space-x-2"
              disabled={saving}
            >
              {saving ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{saving ? t('editList.saving') : t('editList.saveChanges')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditList;
